import { type Page, toListQuery } from '@hello-aria/makalu-api-client'
import type { PaymentDestination, RepaymentDestination } from '@/shared/models/bankAcccountModel'
import { toPaymentDestination, toPaymentDestinationBody } from '@/shared/models/bankAcccountModel'
import type { Company } from '@/shared/models/companyModel'
import type {
    DeleteUser,
    PostUser,
    UpdateUser,
    UpdateUserGateway,
    UpdateUserPaymentDestination,
    User
} from '@/shared/models/userModel'
import { toPostUserBody, toUser } from '@/shared/models/userModel'
import { makaluApi } from '@/shared/utils/makaluApiClient'
import { trackEvent } from '@/shared/utils/analyticsUtils'
import { batchFetch, fetchAllPages, requirePathParam } from '@/shared/utils/requestsUtils'
import { mapSort } from '@/shared/utils/sortUtils'
import type { QueryStringInput } from '@hello-aria/query-string'
import { isEqual, isMatch, isNil, pickBy } from 'lodash'

interface GetUsers {
    companyId: Company['id']
    query?: QueryStringInput
    signal?: AbortSignal
}

export const listUsers = batchFetch(async (options: GetUsers): Promise<Page<User>> => {
    const { companyId, query } = options || {}

    const response = await makaluApi.listPage('/companies/{companyId}/users', {
        params: {
            path: { companyId },
            query: toListQuery({
                ...query,
                sorts: mapSort(query?.sorts, {
                    usage: 'api'
                })
            })
        },
        signal: options.signal
    })

    return { ...response, body: response.body.map(toUser) }
})

interface GetUser {
    companyId: string
    userId?: string
    hasBankAccount?: boolean
}

export const getUser = async (params: GetUser): Promise<User | undefined> => {
    const { companyId, userId } = params
    if (!userId) return
    const response = await makaluApi.GET('/companies/{companyId}/user/{userId}', {
        params: {
            path: { companyId, userId },
            query: toListQuery({ params: { has_bank_account: String(params.hasBankAccount) } })
        }
    })

    return toUser(response)
}

/*
 * makalu-api answers the repayment destinations of a user page by page, newest first. This reads every
 * page, as the block lists them all. It fails closed: a pay-worker error is a 500, never an empty list,
 * so an empty list means the user has no account.
 */
export const listUserRepaymentDestinations = (params: {
    companyId: Company['id']
    userId: User['id']
    signal?: AbortSignal
}): Promise<RepaymentDestination[]> => {
    const { companyId, userId, signal } = params
    return fetchAllPages(
        (query, pageSignal) =>
            makaluApi.listPage('/companies/{companyId}/users/{userId}/repayment-destinations', {
                params: { path: { companyId, userId }, query: toListQuery(query) },
                signal: pageSignal
            }),
        {},
        signal
    )
}

export const createUser = async (payload: PostUser, companyId: Company['id']): Promise<User | undefined> => {
    const formattedUser = toPostUserBody(payload)
    const response = await makaluApi.POST('/companies/{companyId}/user', {
        params: { path: { companyId } },
        body: formattedUser
    })
    trackEvent('Create_User')
    return response
}

export const updateUser = async (payload: UpdateUser, companyId: Company['id']): Promise<User | undefined> => {
    const formattedPayload: UpdateUserGateway = payload
    const response = await makaluApi.PATCH('/companies/{companyId}/user/{userId}', {
        params: { path: { companyId, userId: payload.id } },
        body: formattedPayload
    })

    trackEvent('Update_User')

    return response
}

type State = 'create' | 'delete'

function removePaymentDestinationFromList(
    paymentDestinations: PaymentDestination[],
    paymentDestination: PaymentDestination
): PaymentDestination[] {
    const toRemoveCriteria = pickBy(
        {
            accountNumber: paymentDestination.accountNumber,
            routingNumber: paymentDestination.routingNumber
        },
        (value) => !isNil(value)
    )
    return paymentDestinations.filter((destination) => !isMatch(destination, toRemoveCriteria))
}

export const updateUserPaymentDestinations = async (
    payload: UpdateUserPaymentDestination,
    companyId: Company['id'],
    state: State
): Promise<User | undefined> => {
    let formattedPayload: PaymentDestination[] = []
    const paymentDestinations = payload.user.paymentDestinations ?? []

    if (state === 'create') {
        const isDuplicate = paymentDestinations.some((destination) =>
            isEqual(destination.accountNumber, payload.paymentDestination.accountNumber)
        )
        if (isDuplicate) return Promise.reject(new Error('DUPLICATE_PAYMENT_DESTINATION'))

        const mappedPaymentDestination = toPaymentDestination(payload.paymentDestination)
        formattedPayload = paymentDestinations.concat(mappedPaymentDestination)
    }

    if (state === 'delete')
        formattedPayload = removePaymentDestinationFromList(paymentDestinations, payload.paymentDestination)

    const response = await makaluApi.PATCH('/companies/{companyId}/user/{userId}', {
        params: { path: { companyId, userId: payload.user.id } },
        body: { paymentDestinations: formattedPayload.map(toPaymentDestinationBody) }
    })

    trackEvent('Update_User')

    return response
}

export const deleteUser = async (companyId: Company['id'], payload: DeleteUser): Promise<User | undefined> => {
    const response = await makaluApi.DELETE('/companies/{companyId}/user/{userId}', {
        params: { path: { companyId, userId: requirePathParam('userId', payload.userId) } }
    })

    trackEvent('Delete_User')

    return response
}
